import {Box, Divider} from 'native-base';
import {CustomText, H3Text, H6Text} from '../../../../components/core/text';
import {Dimensions, StyleSheet} from 'react-native';
import React, {useCallback, useEffect, useState} from 'react';
import {formatCurrency, isItemOrganic} from '../../../../utils/common';

import BinIcon from '@buncha/components/core/icon/BinIcon';
import DropItemModalBody from '../DropItemModalBody/DropItemModalBody';
import If from '../../../../../src/components/conditional/If';
import ItemName from '@buncha/batchDetails/component/ItemName';
import ItemQuantityPill from '../../../../components/composites/ItemQuantityPill/ItemQuantityPill';
import ItemTypePill from '@buncha/batchDetails/component/ItemName/ItemTypePill';
import {Modules} from '@buncha/utils/types';
import {OrderItem} from '@buncha/itemDetails/types/ItemScanner';
import OrganicReminderModal from '../organicReminderModal';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {Touchable} from '@buncha/components/core/touchable';
import VerifyUPCPill from '../VerifyUPCPill';
import font from '@buncha/appStyles/style/font';
import {useColorThemeType} from '../../../../appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useItemDetailsContext} from '@buncha/itemDetails/hooks/ItemDetails';

interface ItemInfoProps {
  item?: OrderItem;
  isUPCMode: boolean;
  upcItemId?: number;
  orderItemId: number;
  esIndex: string;
  isItemDetailPage: boolean;
  isB2BStore?: boolean;
  storeMap?: string;
  storeId?: number;
}

const ItemInfo = (props: ItemInfoProps) => {
  const {state} = useItemDetailsContext();
  const theme = useColorThemeType('main');
  const [modalOpen, setModalOpen] = useState(false);
  const isGlutenFree = false;

  const updatedPrice = state.buyersForItem?.[0]?.updatedPricePerQuantity;
  const price = updatedPrice || props?.item?.price;
  const storeId = props?.storeId;
  const {body} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const isB2BStore = useIsMeijerStore(storeId);
  const isOrganic = isItemOrganic(props?.item?.name ?? '') && !isB2BStore;

  const [showModal, setShowModal] = useState(false);

  const toggleDeleteModal = useCallback(() => {
    setShowModal(!showModal);
  }, [showModal]);

  const toggleModal = useCallback(() => {
    setModalOpen(!modalOpen);
  }, [modalOpen]);

  useEffect(() => {
    if (isGlutenFree) {
      let timer = setTimeout(() => {
        setModalOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isGlutenFree]);

  return (
    <Box style={styles.parentBox}>
      <Box style={styles.organicPillContainer}>
        <ItemTypePill
          size={font.FontSize.fontH5.fontSize}
          isOrganic={isOrganic}
          itemType={props?.item?.itemType}
        />
      </Box>
      <ItemName
        name={props?.item?.name ?? ''}
        size={font.FontSize.fontH3.fontSize}
        nameStyle={styles.ItemNameTypo}
      />
      <If condition={props.item?.options}>
        <H6Text style={[styles.ItemNameTypo, {color: theme?.text.blue[100]}]}>
          {props.item?.options}
        </H6Text>
      </If>
      <Box style={[styles.BottomContainer]}>
        <H3Text style={[styles.ItemNameTypo, {color: theme?.text.blue[300]}]}>
          {formatCurrency(price ?? 0)}
        </H3Text>
        <If condition={props.isUPCMode}>
          <VerifyUPCPill />
        </If>
        <If condition={!props.isUPCMode}>
          <ItemQuantityPill
            quantity={
              props.item?.totalupdatedQuantityByShopper ||
              props.item?.quantity ||
              0
            }
          />
        </If>
      </Box>
      <If condition={props.isUPCMode}>
        <Box style={[styles.unableToDropContainer]}>
          <Divider />
          <Touchable style={[styles.binContainer]} onPress={toggleDeleteModal}>
            <Box>
              <BinIcon
                viewBox="-5 -5 25 25"
                fill={theme?.background.red[100]}
              />
            </Box>
            <Box style={[styles.typoContainer, styles.jcC]}>
              <H6Text
                style={{
                  color: theme?.text?.red[100],
                }}>
                {body?.unableToFindItem}
              </H6Text>
            </Box>
          </Touchable>
          <Divider />
        </Box>
        <If condition={!props?.isUPCMode}>
          <CustomText
            style={[
              styles.quanitityText,
              {
                color: theme?.text.red[100],
              },
            ]}>
            {body?.forAllCustomers}
          </CustomText>
        </If>
      </If>
      <DropItemModalBody
        orderItemId={props?.orderItemId}
        onClose={toggleDeleteModal}
        open={showModal}
        isUPCMode={props?.isUPCMode}
        catalogItemId={props.item?.catalogItemId}
        orderId={props.item?.order?.id || 0}
      />
      <If condition={modalOpen && props?.isItemDetailPage}>
        <OrganicReminderModal
          onClose={toggleModal}
          open={modalOpen}
          isOrganic={isOrganic}
          isGlutenFree={isGlutenFree}
        />
      </If>
    </Box>
  );
};

export default React.memo(ItemInfo);

const styles = StyleSheet.create({
  ItemNameTypo: {
    textAlign: 'left',
  },
  BottomContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  parentBox: {
    marginTop: 5,
  },
  quanitityText: {
    textAlign: 'right',
  },
  unableToDropContainer: {
    marginTop: 20,
  },
  binContainer: {
    justifyContent: 'flex-start',
    paddingVertical: 10,
    flexDirection: 'row',
  },
  typoContainer: {
    marginLeft: 2,
  },
  jcC: {
    justifyContent: 'center',
  },
  fullWidth: {
    width: Dimensions.get('screen').width,
  },
  locationInfoBox: {
    marginTop: 10,
  },
  locationContainer: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: 2,
    borderRadius: 5,
    paddingLeft: 10,
    marginTop: 10,
  },
  organicPillContainer: {alignSelf: 'center', marginBottom: 5},
  storeMapBox: {
    flexDirection: 'row',
    marginTop: 10,
    marginBottom: 5,
    paddingTop: 6,
    paddingBottom: 8,
    paddingHorizontal: 15,
    gap: 5,
    borderRadius: 10,
    alignSelf: 'flex-start',
  },
});
